import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Card from 'react-credit-cards';
import NumberFormat from 'react-number-format';
import { Formik } from 'formik';
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField
} from '@material-ui/core';
import { replaceUserCreditCardApi } from 'api';
import { BackdropCircular } from 'components';
import { states } from 'helpers/usStates';
import { ccValidationSchema } from './ccValidationSchema';


const CreditCardForm = ({creditCardData, setCreditCardData}) => {
  const dispatch = useDispatch();
  const { master_id } = creditCardData;
  const [focused, setFocused] = useState('');
  const [ccNumberValidationData, setCCNumberValidationData] = useState({issuer: '', maxLength: 16});
  const { loadingUserCreditCards } = useSelector((state: IReduxState) => state.user);

  const handleSaveCreditCard = async (values) => {
    const [ccExpMonth, ccExpYear] = values.expiry.split('/');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {expiry, ...restData} = values;
    const ccData = {...restData, ccExpMonth, ccExpYear};
    await replaceUserCreditCardApi({dispatch, ccData, masterId: master_id});
    setCreditCardData({master_id: null, action: null});
  };

  return (
    <>
      <Formik
        initialValues={{
          ccNum: '',
          ccExpYear: '',
          ccExpMonth: '',
          expiry: '',
          cvv: '',
          firstName: '',
          lastName: '',
          regionCode: '',
          regionName: '',
          regionId: '',
          street: ['', ''],
          telephone: '',
          postcode: '',
          countryId: 'US',
          city: '',
          company: '',
          replaceForSubscriptions: false,
        }}
        onSubmit={handleSaveCreditCard}
        validationSchema={ccValidationSchema(ccNumberValidationData)}
      >
        {({
          values,
          errors,
          touched,
          handleSubmit,
          handleChange,
          handleBlur,
          setFieldValue
        }) => (
          <form
            noValidate
            onSubmit={handleSubmit}
            className="relative"
          >
            <div className="card-container max-w-600 mx-auto px-7 pb-7 pt-15 text-primary-gray">
              <Card
                name={`${values.firstName} ${values.lastName}`}
                number={values.ccNum}
                expiry={values.expiry}
                cvc={values.cvv}
                preview
                focused={focused}
                callback={({issuer, maxLength}) => setCCNumberValidationData({issuer, maxLength})}
              />
              <Grid container spacing={2} className="mt-2">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    variant="outlined"
                    name="firstName"
                    label="First Name"
                    value={values.firstName}
                    onChange={handleChange}
                    onBlur={(event) => {
                      handleBlur(event);
                      setFocused('');
                    }}
                    onFocus={() => setFocused('name')}
                    error={touched?.firstName && Boolean(errors?.firstName)}
                    helperText={touched?.firstName && errors?.firstName}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    variant="outlined"
                    name="lastName"
                    label="Last Name"
                    value={values.lastName}
                    onChange={handleChange}
                    onBlur={(event) => {
                      handleBlur(event);
                      setFocused('');
                    }}
                    onFocus={() => setFocused('name')}
                    error={touched?.lastName && Boolean(errors?.lastName)}
                    helperText={touched?.lastName && errors?.lastName}
                  />
                </Grid>
                <Grid item xs={12}>
                  <NumberFormat
                    customInput={TextField}
                    mask="_"
                    required
                    className="w-full"
                    variant="outlined"
                    name="ccNum"
                    label="Credit Card Number"
                    value={values.ccNum}
                    onChange={handleChange}
                    onBlur={(event) => {
                      handleBlur(event);
                      setFocused('');
                    }}
                    onFocus={() => setFocused('number')}
                    error={touched?.ccNum && Boolean(errors?.ccNum)}
                    helperText={touched?.ccNum && errors?.ccNum}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <NumberFormat
                    customInput={TextField}
                    format="##/##"
                    mask="_"
                    required
                    className="w-full"
                    variant="outlined"
                    name="expiry"
                    label="Expiration Date"
                    value={values.expiry}
                    onChange={handleChange}
                    onBlur={(event) => {
                      handleBlur(event);
                      setFocused('');
                    }}
                    onFocus={() => setFocused('expiry')}
                    error={touched?.expiry && Boolean(errors?.expiry)}
                    helperText={touched?.expiry && errors?.expiry}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <NumberFormat
                    customInput={TextField}
                    format="####"
                    required
                    className="w-full"
                    variant="outlined"
                    name="cvv"
                    label="CVV"
                    value={values.cvv}
                    onChange={(event) => setFieldValue('cvv', event.target.value?.trim())}
                    onBlur={(event) => {
                      setFieldValue('cvv', event.target.value?.trim());
                      setFocused('');
                    }}
                    onFocus={() => setFocused('cvc')}
                    error={touched?.cvv && Boolean(errors?.cvv)}
                    helperText={touched?.cvv && errors?.cvv}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider className="my-2" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    variant="outlined"
                    name="street[0]"
                    label="Street Address 1"
                    value={values.street[0]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched?.street?.[0] && Boolean(errors?.street?.[0])}
                    helperText={touched?.street?.[0] && errors?.street?.[0]}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    name="street[1]"
                    label="Street Address 2"
                    value={values.street[1]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched?.street?.[1] && Boolean(errors?.street?.[1])}
                    helperText={touched?.street?.[1] && errors?.street?.[1]}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    variant="outlined"
                    name="city"
                    label="City"
                    value={values.city}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched?.city && Boolean(errors?.city)}
                    helperText={touched?.city && errors?.city}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    select
                    variant="outlined"
                    name="regionId"
                    label="State"
                    value={values.regionId}
                    onChange={(event) => {
                      const {value} = event.target;
                      const {name, shortName} = states.find((state) => state.value === value);
                      handleChange(event);
                      setFieldValue('regionCode', shortName);
                      setFieldValue('regionName', name);
                    }}
                    onBlur={handleBlur}
                    error={touched?.regionId && Boolean(errors?.regionId)}
                    helperText={touched?.regionId && errors?.regionId}
                  >
                    {states.map((state) => (
                      <MenuItem key={state.value} value={state.value}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    variant="outlined"
                    name="postcode"
                    label="Zip Code"
                    value={values.postcode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched?.postcode && Boolean(errors?.postcode)}
                    helperText={touched?.postcode && errors?.postcode}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <NumberFormat
                    customInput={TextField}
                    format="(###) ###-####"
                    mask="_"
                    required
                    className="w-full"
                    label="Phone"
                    variant="outlined"
                    name="telephone"
                    value={values.telephone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched?.telephone && Boolean(errors?.telephone)}
                    helperText={touched?.telephone && errors?.telephone}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    name="company"
                    label="Company"
                    value={values.company}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched?.company && Boolean(errors?.company)}
                    helperText={touched?.company && errors?.company}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider className="my-2" />
                </Grid>
                <Grid item xs={12} className="px-5">
                  <FormControlLabel
                    className="items-start"
                    control={
                      <Checkbox
                        size="small"
                        name="replaceForSubscriptions"
                        color="primary"
                        checked={values.replaceForSubscriptions}
                        onChange={handleChange}
                      />
                    }
                    label={
                      <strong className="flex py-2">
                        <span>
                          {'I understand that this action will apply a current card to all my subscriptions.'}
                          <span className="text-error">*</span>
                        </span>
                      </strong>
                    }
                  />
                </Grid>
                <Grid item xs={12} className="flex">
                  <Button
                    fullWidth
                    variant="outlined"
                    className="text-muted mr-2"
                    onClick={() => setCreditCardData({master_id: null, action: ''})}
                  >
                    Cancel
                  </Button>
                  <Button
                    fullWidth
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={!values.replaceForSubscriptions}
                  >
                    Submit
                  </Button>
                </Grid>
              </Grid>
            </div>
          </form>
        )
        }
      </Formik>
      <BackdropCircular loading={loadingUserCreditCards} />
    </>
  );
};

export default CreditCardForm;
